import type {
  AssistantDetectResult,
  AssistantEvent,
  AssistantExecRequest,
  AssistantExecResult,
  AssistantModelList,
  AssistantModelPick,
  AssistantProviderId,
  AssistantProviderStatus,
  AssistantTurnRequest,
  AssistantTurnResult,
  AssistantConnection,
  AssistantInspectResult,
} from "./assistant";
import type { ImportedExtension } from "./vscode-content";
/**
 * App desktop (Electron, `apps/desktop`): a mesma interface da web numa janela nativa, com barra de
 * título no estilo do VS Code. Os menus são definidos aqui uma vez só: o processo principal monta o
 * menu nativo (atalhos e a barra de menus do macOS) e a web desenha os mesmos menus na barra de
 * título (Windows e Linux). Ver ADR 0010.
 */

/** Altura da barra de título (a página a desenha; os botões da janela usam a mesma altura). */
export const DESKTOP_TITLE_BAR_HEIGHT = 44;

/** Ações que o próprio Electron executa na janela (papéis de menu). */
export const DESKTOP_ROLES = [
  "undo",
  "redo",
  "cut",
  "copy",
  "paste",
  "selectAll",
  "zoomIn",
  "zoomOut",
  "resetZoom",
  "togglefullscreen",
  "reload",
  "minimize",
  "toggleMaximize",
  "close",
  "quit",
] as const;
export type DesktopRole = (typeof DESKTOP_ROLES)[number];

/** Comandos do app. Os de {@link MAIN_COMMANDS} rodam no processo principal; o resto, na página. */
export const DESKTOP_COMMANDS = [
  // Arquivo
  "new-file",
  "new-folder",
  "new-project",
  "open-project",
  "open-folder",
  "import-repo",
  "save-as",
  "share-project",
  "new-window",
  "logout",
  // Exibir
  "command-palette",
  "quick-open",
  "global-search",
  "panel-files",
  "panel-search",
  "panel-git",
  "panel-people",
  "panel-chat",
  "panel-history",
  "panel-run",
  "panel-extensions",
  "panel-activity",
  "panel-problems",
  "panel-output",
  "panel-comments",
  "toggle-sidebar",
  "toggle-right",
  "toggle-panel",
  "toggle-chat",
  "toggle-terminal",
  "split-editor",
  "layout-default",
  "layout-focus",
  "layout-collab",
  "layout-reset",
  "editor-font-bigger",
  "editor-font-smaller",
  "editor-font-reset",
  "refresh",
  "reload-window",
  // Editor
  "close-editor",
  "next-editor",
  "prev-editor",
  "go-symbol",
  "go-line",
  "comment-line",
  "share-selection",
  "format-document",
  "toggle-breakpoint",
  // Ir
  "go-projects",
  "go-chat",
  "go-discover",
  "go-groups",
  "go-plugins",
  "go-profile",
  "go-back",
  "go-forward",
  // Terminal
  "run-project",
  "run-file",
  "debug-file",
  "stop-project",
  "restart-terminal",
  "new-terminal",
  "kill-terminal",
  // Configurações
  "settings",
  "project-settings",
  "color-themes",
  "app-icon",
  "import-vscode",
  "shortcuts",
  "change-server",
  // Ajuda
  "show-tour",
  "check-updates",
  "about",
] as const;
export type DesktopCommand = (typeof DESKTOP_COMMANDS)[number];

/** Comandos que o processo principal resolve sozinho (não dependem da página aberta). */
export const MAIN_COMMANDS: readonly DesktopCommand[] = ["new-window", "check-updates", "about"];

/** Comandos que só existem dentro de um projeto aberto (o menu os desabilita fora dele). */
export const WORKSPACE_COMMANDS: readonly DesktopCommand[] = [
  "new-file",
  "new-folder",
  "panel-files",
  "panel-search",
  "panel-git",
  "panel-people",
  "panel-chat",
  "panel-history",
  "panel-run",
  "panel-activity",
  "panel-problems",
  "panel-output",
  "panel-comments",
  "toggle-sidebar",
  "toggle-right",
  "toggle-panel",
  "toggle-terminal",
  "split-editor",
  "layout-default",
  "layout-focus",
  "layout-collab",
  "layout-reset",
  "close-editor",
  "next-editor",
  "prev-editor",
  "go-symbol",
  "go-line",
  "comment-line",
  "share-selection",
  "format-document",
  "toggle-breakpoint",
  "save-as",
  "run-project",
  "run-file",
  "debug-file",
  "stop-project",
  "restart-terminal",
  "new-terminal",
  "kill-terminal",
  "project-settings",
];

export type DesktopMenuItem =
  | { type: "separator" }
  | {
      command: DesktopCommand;
      label: string;
      accelerator?: string;
      /**
       * O atalho é tratado pela página, não pelo menu nativo: assim o terminal continua recebendo
       * a tecla quando está em foco (Ctrl+W apaga palavra, Ctrl+G cancela, Ctrl+\ é SIGQUIT).
       */
      page?: boolean;
    }
  | { role: DesktopRole; label: string; accelerator?: string }
  /** Submenu (ex.: Exibir › Painéis), para o menu não virar uma lista enorme. */
  | { label: string; submenu: DesktopMenuItem[] };

/** Itens de um menu sem os submenus (cada item com comando ou papel aparece uma vez). */
export function flatMenuItems(items: DesktopMenuItem[]): DesktopMenuItem[] {
  return items.flatMap((i) => ("submenu" in i ? flatMenuItems(i.submenu) : [i]));
}

export interface DesktopMenu {
  id: string;
  label: string;
  items: DesktopMenuItem[];
}

const SEP = { type: "separator" } as const;

/** Menus da janela (pt-BR). Atalhos no formato do Electron ("CmdOrCtrl+Shift+P"). */
export const DESKTOP_MENUS: DesktopMenu[] = [
  {
    id: "file",
    label: "Arquivo",
    items: [
      { command: "new-file", label: "Novo arquivo", accelerator: "CmdOrCtrl+N" },
      { command: "new-folder", label: "Nova pasta" },
      { command: "new-project", label: "Novo projeto…", accelerator: "CmdOrCtrl+Alt+N" },
      SEP,
      { command: "open-project", label: "Abrir projeto…", accelerator: "CmdOrCtrl+O" },
      { command: "open-folder", label: "Abrir pasta local…" },
      { command: "import-repo", label: "Importar repositório Git…" },
      SEP,
      { command: "save-as", label: "Salvar como…", accelerator: "CmdOrCtrl+Shift+S" },
      { command: "share-project", label: "Compartilhar projeto…" },
      { command: "new-window", label: "Nova janela", accelerator: "CmdOrCtrl+Shift+N" },
      SEP,
      { command: "close-editor", label: "Fechar editor", accelerator: "CmdOrCtrl+W", page: true },
      { command: "logout", label: "Sair da conta" },
      { role: "close", label: "Fechar janela", accelerator: "CmdOrCtrl+Shift+W" },
    ],
  },
  {
    id: "edit",
    label: "Editar",
    items: [
      { role: "undo", label: "Desfazer", accelerator: "CmdOrCtrl+Z" },
      { role: "redo", label: "Refazer", accelerator: "CmdOrCtrl+Shift+Z" },
      SEP,
      { role: "cut", label: "Recortar", accelerator: "CmdOrCtrl+X" },
      { role: "copy", label: "Copiar", accelerator: "CmdOrCtrl+C" },
      { role: "paste", label: "Colar", accelerator: "CmdOrCtrl+V" },
      SEP,
      { role: "selectAll", label: "Selecionar tudo", accelerator: "CmdOrCtrl+A" },
      SEP,
      { command: "panel-search", label: "Buscar no projeto", accelerator: "CmdOrCtrl+Shift+F" },
      { command: "format-document", label: "Formatar documento", accelerator: "Shift+Alt+F" },
      { command: "comment-line", label: "Comentar na linha…" },
      { command: "share-selection", label: "Enviar seleção ao chat do time" },
    ],
  },
  {
    id: "view",
    label: "Exibir",
    items: [
      { command: "command-palette", label: "Paleta de comandos…", accelerator: "CmdOrCtrl+Shift+P" },
      { command: "quick-open", label: "Ir para arquivo ou projeto…", accelerator: "CmdOrCtrl+P" },
      {
        command: "global-search",
        label: "Busca global…",
        accelerator: "CmdOrCtrl+K",
        page: true,
      },
      // Ctrl+R no terminal é a busca no histórico do shell: a página trata o atalho.
      { command: "refresh", label: "Atualizar", accelerator: "CmdOrCtrl+R", page: true },
      SEP,
      {
        label: "Painéis",
        submenu: [
          { command: "panel-files", label: "Explorador", accelerator: "CmdOrCtrl+Shift+E" },
          { command: "panel-search", label: "Buscar e substituir", accelerator: "CmdOrCtrl+Shift+F" },
          { command: "panel-git", label: "Controle do código-fonte", accelerator: "CmdOrCtrl+Shift+G" },
          { command: "panel-run", label: "Executar e depurar", accelerator: "CmdOrCtrl+Shift+D" },
          { command: "panel-extensions", label: "Extensões", accelerator: "CmdOrCtrl+Shift+X" },
          SEP,
          { command: "panel-problems", label: "Problemas", accelerator: "CmdOrCtrl+Shift+M" },
          { command: "panel-output", label: "Saída", accelerator: "CmdOrCtrl+Shift+U" },
          { command: "panel-comments", label: "Comentários" },
        ],
      },
      {
        label: "Colaboração",
        submenu: [
          { command: "panel-people", label: "Pessoas no projeto" },
          { command: "panel-chat", label: "Chat do projeto" },
          { command: "panel-activity", label: "Atividade do projeto" },
          { command: "panel-history", label: "Histórico de alterações" },
        ],
      },
      {
        label: "Layout",
        submenu: [
          { command: "toggle-sidebar", label: "Barra lateral", accelerator: "CmdOrCtrl+B" },
          { command: "toggle-panel", label: "Painel inferior", accelerator: "CmdOrCtrl+J" },
          { command: "toggle-right", label: "Lateral direita", accelerator: "CmdOrCtrl+Alt+B" },
          { command: "split-editor", label: "Dividir editor", accelerator: "CmdOrCtrl+\\", page: true },
          SEP,
          { command: "layout-default", label: "Padrão" },
          { command: "layout-focus", label: "Foco no editor" },
          { command: "layout-collab", label: "Colaboração" },
          { command: "layout-reset", label: "Restaurar layout padrão" },
        ],
      },
      {
        label: "Aparência",
        submenu: [
          { role: "zoomIn", label: "Aumentar zoom", accelerator: "CmdOrCtrl+=" },
          { role: "zoomOut", label: "Diminuir zoom", accelerator: "CmdOrCtrl+-" },
          { role: "resetZoom", label: "Zoom padrão", accelerator: "CmdOrCtrl+0" },
          SEP,
          { command: "editor-font-bigger", label: "Aumentar a letra do editor" },
          { command: "editor-font-smaller", label: "Diminuir a letra do editor" },
          { command: "editor-font-reset", label: "Letra do editor padrão" },
        ],
      },
      SEP,
      { command: "toggle-chat", label: "Chat com IA (Claude · Copilot)", accelerator: "CmdOrCtrl+Alt+I" },
      { role: "togglefullscreen", label: "Tela cheia", accelerator: "F11" },
    ],
  },
  {
    id: "go",
    label: "Ir",
    items: [
      { command: "go-back", label: "Voltar", accelerator: "Alt+Left" },
      { command: "go-forward", label: "Avançar", accelerator: "Alt+Right" },
      SEP,
      { command: "go-symbol", label: "Ir para símbolo no arquivo…", accelerator: "CmdOrCtrl+Shift+O" },
      { command: "go-line", label: "Ir para linha…", accelerator: "Ctrl+G", page: true },
      { command: "next-editor", label: "Próximo editor", accelerator: "Ctrl+Tab", page: true },
      { command: "prev-editor", label: "Editor anterior", accelerator: "Ctrl+Shift+Tab", page: true },
      SEP,
      { command: "go-projects", label: "Projetos" },
      { command: "go-chat", label: "Mensagens" },
      { command: "go-discover", label: "Descobrir devs" },
      { command: "go-groups", label: "Grupos" },
      { command: "go-profile", label: "Meu perfil" },
    ],
  },
  {
    id: "terminal",
    label: "Terminal",
    items: [
      { command: "new-terminal", label: "Novo terminal", accelerator: "Ctrl+Shift+`" },
      { command: "toggle-terminal", label: "Mostrar terminal", accelerator: "Ctrl+`" },
      SEP,
      { command: "run-project", label: "Executar projeto", accelerator: "F5" },
      { command: "run-file", label: "Executar arquivo atual", accelerator: "CmdOrCtrl+F5" },
      { command: "debug-file", label: "Depurar arquivo atual", accelerator: "Alt+F5" },
      { command: "toggle-breakpoint", label: "Alternar ponto de parada", accelerator: "F9", page: true },
      { command: "stop-project", label: "Interromper", accelerator: "Shift+F5" },
      SEP,
      { command: "restart-terminal", label: "Reiniciar terminal" },
      { command: "kill-terminal", label: "Encerrar terminal" },
    ],
  },
  {
    id: "settings",
    label: "Configurações",
    items: [
      { command: "settings", label: "Conta e perfil", accelerator: "CmdOrCtrl+," },
      { command: "project-settings", label: "Configurações do projeto" },
      SEP,
      { command: "go-plugins", label: "Loja de plugins" },
      { command: "color-themes", label: "Temas (claro e cores)…" },
      { command: "app-icon", label: "Cor do ícone do app…" },
      { command: "import-vscode", label: "Importar plugins do VS Code…" },
      { command: "shortcuts", label: "Atalhos de teclado" },
      SEP,
      { command: "change-server", label: "Servidor do Kompil…" },
    ],
  },
  {
    id: "help",
    label: "Ajuda",
    items: [
      { command: "show-tour", label: "Tutorial do Kompil" },
      { command: "check-updates", label: "Procurar atualizações…" },
      { command: "reload-window", label: "Recarregar janela", accelerator: "CmdOrCtrl+Shift+R", page: true },
      SEP,
      { command: "about", label: "Sobre o Kompil" },
    ],
  },
];

/** Atalhos com dois passos ("Ctrl+K Ctrl+S") são só exibidos; o menu nativo não os registra. */
export const isChord = (accelerator: string) => accelerator.includes(" ");

/** O que {@link matchesAccelerator} lê de um KeyboardEvent. */
export interface KeyLike {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

/**
 * O evento de teclado corresponde ao atalho do Electron ("CmdOrCtrl+W", "Ctrl+Shift+Tab")?
 * Usado nos atalhos tratados pela página (itens `page` dos menus). Atalhos de dois passos nunca.
 */
export function matchesAccelerator(accelerator: string, e: KeyLike, mac: boolean): boolean {
  if (isChord(accelerator)) return false;
  const parts = accelerator.split("+").map((k) => (k === "" ? "+" : k));
  const key = parts.pop()!;
  const want = { ctrl: false, meta: false, alt: false, shift: false };
  for (const m of parts) {
    if (m === "CmdOrCtrl") want[mac ? "meta" : "ctrl"] = true;
    else if (m === "Cmd") want.meta = true;
    else if (m === "Ctrl") want.ctrl = true;
    else if (m === "Alt") want.alt = true;
    else if (m === "Shift") want.shift = true;
  }
  if (e.ctrlKey !== want.ctrl || e.metaKey !== want.meta || e.altKey !== want.alt) return false;
  if (e.shiftKey !== want.shift) return false;
  return e.key.toLowerCase() === key.toLowerCase();
}

/** Itens de menu cujo atalho a página trata (ver `page`). */
export const PAGE_ACCELERATORS: ReadonlyArray<{ command: DesktopCommand; accelerator: string }> =
  DESKTOP_MENUS.flatMap((m) => flatMenuItems(m.items)).flatMap((i) =>
    "command" in i && i.page && i.accelerator ? [{ command: i.command, accelerator: i.accelerator }] : [],
  );

const MAC_KEYS: Record<string, string> = {
  CmdOrCtrl: "⌘",
  Cmd: "⌘",
  Ctrl: "⌃",
  Alt: "⌥",
  Shift: "⇧",
  Left: "←",
  Right: "→",
};

/** "CmdOrCtrl+Shift+P" → "Ctrl+Shift+P" (Windows/Linux) ou "⇧⌘P" (macOS). */
export function formatAccelerator(accelerator: string, mac: boolean): string {
  return accelerator
    .split(" ")
    .map((chord) => {
      const keys = chord.split("+").map((k) => (k === "" ? "+" : k));
      if (!mac) return keys.map((k) => (k === "CmdOrCtrl" || k === "Cmd" ? "Ctrl" : k)).join("+");
      // macOS: modificadores na ordem do sistema (⌃⌥⇧⌘), sem separador.
      const order = ["Ctrl", "Alt", "Shift", "CmdOrCtrl", "Cmd"];
      const mods = keys.filter((k) => order.includes(k)).sort((a, b) => order.indexOf(a) - order.indexOf(b));
      const rest = keys.filter((k) => !order.includes(k));
      return [...mods.map((k) => MAC_KEYS[k]), ...rest.map((k) => MAC_KEYS[k] ?? k)].join("");
    })
    .join(" ");
}

/** Cores da janela nativa (barra de título e botões) — vêm do tema ativo da página. */
export interface DesktopThemeColors {
  background: string;
  foreground: string;
  dark: boolean;
  /**
   * Demais cores da interface (#rrggbb): as vistas do motor VS Code embutidas (Claude, ferramentas
   * das extensões) se pintam com elas e acompanham o tema da IDE inteira.
   */
  ui?: {
    surface: string;
    panel: string;
    muted: string;
    emphasis: string;
    border: string;
    fgMuted: string;
    accent: string;
    accentFg: string;
  };
  /**
   * Tema de cor do VS Code em uso (rótulo do tema, como "Tokyo Night"), quando vem de uma extensão:
   * o motor usa o mesmo tema nas vistas embutidas.
   */
  vscodeTheme?: string | null;
}

/** Cor do ícone do app (área de trabalho, barra de tarefas, Dock, bandeja). */
export type AppIconColor = "black" | "white";

// ───────────── GitHub Copilot (servidor de linguagem oficial, só no app desktop) ─────────────

export interface LspPosition {
  line: number;
  character: number;
}

/** Sugestão inline do Copilot (`textDocument/inlineCompletion`). */
export interface CopilotItem {
  insertText: string;
  range: { start: LspPosition; end: LspPosition };
  command?: { command: string; title?: string; arguments?: unknown[] };
}

export type CopilotState = "starting" | "signed-out" | "ready" | "unavailable" | "error";

export interface CopilotStatus {
  state: CopilotState;
  /** Conta do GitHub conectada. */
  user?: string;
  message?: string;
}

export interface CopilotRequest {
  /** Identificador do arquivo (ex.: kompil://<projeto>/<caminho>). */
  uri: string;
  languageId: string;
  text: string;
  line: number;
  character: number;
  tabSize: number;
  insertSpaces: boolean;
}

export interface CopilotSignIn {
  ok: boolean;
  /** Código para digitar em github.com/login/device. */
  userCode?: string;
  verificationUri?: string;
  alreadySignedIn?: boolean;
  error?: string;
}

/** Posição LSP (linha e coluna em unidades UTF-16, como as strings do JS) → índice no texto. */
export function lspOffset(text: string, pos: LspPosition): number {
  let offset = 0;
  for (let line = 0; line < pos.line; line++) {
    const next = text.indexOf("\n", offset);
    if (next < 0) return text.length;
    offset = next + 1;
  }
  const lineEnd = text.indexOf("\n", offset);
  return Math.min(offset + pos.character, lineEnd < 0 ? text.length : lineEnd);
}

/** Índice no texto → posição LSP. */
export function lspPosition(text: string, offset: number): LspPosition {
  const before = text.slice(0, offset);
  const line = before.split("\n").length - 1;
  return { line, character: offset - (before.lastIndexOf("\n") + 1) };
}

/**
 * A sugestão do Copilot pode reescrever do começo da linha até depois do cursor; o editor mostra
 * texto fantasma inserido no cursor. Devolve o pedaço novo, ou null quando mostrar a sugestão
 * exigiria apagar algo que a pessoa escreveu.
 */
export function copilotGhostText(text: string, cursor: number, item: CopilotItem): string | null {
  const start = lspOffset(text, item.range.start);
  const end = lspOffset(text, item.range.end);
  if (start > cursor || end < cursor) return null;
  const typed = text.slice(start, cursor);
  const replaced = text.slice(cursor, end);
  const insert = item.insertText.replace(/\r\n/g, "\n");
  if (!insert.startsWith(typed)) return null;
  let ghost = insert.slice(typed.length);
  if (replaced) {
    if (ghost.endsWith(replaced)) ghost = ghost.slice(0, ghost.length - replaced.length);
    else if (replaced.trim()) return null;
  }
  return ghost.length > 0 ? ghost : null;
}

/** API da ponte (preload) do app desktop, em `window.kompilDesktop`. Ausente no navegador. */
// ───────────── Agentes de IA (Copilot Chat pelo ACP) ─────────────

/** Agentes de chat que falam o Agent Client Protocol (agentclientprotocol.com). */
export type AgentProvider = "copilot";

export interface AgentToolCall {
  id: string;
  title: string;
  /** read · edit · delete · move · search · execute · think · fetch · other */
  kind?: string;
  status?: "pending" | "in_progress" | "completed" | "failed";
  /** Caminhos do projeto que a ferramenta tocou. */
  paths?: string[];
  /** Saída de texto (ex.: comando no terminal), resumida. */
  output?: string;
}

export interface AgentPermissionRequest {
  requestId: number;
  sessionId: string;
  title: string;
  kind?: string;
  paths?: string[];
  /** Comando a rodar no computador (ferramentas de execução). */
  command?: string;
  options: Array<{
    id: string;
    name: string;
    kind: "allow_once" | "allow_always" | "reject_once" | "reject_always" | string;
  }>;
}

export interface AgentModel {
  id: string;
  name: string;
  /** Ex.: "0x", "1x" — consumo de pedidos premium do Copilot. */
  usage?: string;
}

export type AgentEvent =
  | { type: "status"; state: "starting" | "ready" | "signed-out" | "error" | "unavailable"; message?: string }
  | { type: "chunk"; sessionId: string; kind: "message" | "thought"; text: string }
  | { type: "tool"; sessionId: string; call: AgentToolCall }
  | { type: "plan"; sessionId: string; entries: Array<{ content: string; status: string }> }
  | { type: "permission"; request: AgentPermissionRequest }
  | { type: "file"; projectId: string; path: string; content: string | null }
  | { type: "done"; sessionId: string; stopReason: string; usage?: AgentUsage }
  | { type: "config"; sessionId: string; config: AgentConfigOption[] };

/** Tokens de um turno do agente (quando o servidor informa). */
export interface AgentUsage {
  inputTokens: number;
  outputTokens: number;
}

/** Opção de configuração da sessão do agente (ex.: nível de raciocínio, modo). */
export interface AgentConfigOption {
  id: string;
  name: string;
  current: string;
  options: Array<{ value: string; name: string }>;
}

export interface AgentSession {
  sessionId: string;
  models: AgentModel[];
  model: string | null;
  /** reasoning_effort (none · low · medium · high…) e mode (agente · planejar…), se o agente oferecer. */
  config: AgentConfigOption[];
}

// ───────────── Claude com a conta da Anthropic da pessoa ─────────────

export interface ClaudeChatRequest {
  requestId: string;
  model: string;
  effort: "low" | "medium" | "high";
  /** Instruções e contexto do projeto (vindos de /ai/context). */
  instructions: string;
  context: string;
  messages: Array<{ role: "user" | "assistant"; content: string }>;
}

export type ClaudeEvent =
  | { requestId: string; type: "text" | "thinking"; text: string }
  | {
      requestId: string;
      type: "done";
      stopReason: string | null;
      model: string;
      usage?: ClaudeUsage;
      limits?: ClaudeRateLimits;
    }
  | { requestId: string; type: "error"; message: string; auth?: boolean };

// ───────────── Uso das IAs ─────────────

/** Tokens de uma resposta do Claude (campo `usage` da API). */
export interface ClaudeUsage {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
}

/** Um limite por minuto da conta na Anthropic: quanto cabe, quanto resta e quando enche de novo. */
export interface RateLimitWindow {
  limit: number;
  remaining: number;
  /** ISO 8601. */
  reset?: string;
}

/** Cabeçalhos `anthropic-ratelimit-*` da última resposta (limites da organização, por minuto). */
export interface ClaudeRateLimits {
  requests?: RateLimitWindow;
  tokens?: RateLimitWindow;
  input?: RateLimitWindow;
  output?: RateLimitWindow;
  /** Quando foi lido (ms). */
  at: number;
}

/** Uma cota do Copilot (pedidos premium, chat ou sugestões). */
export interface CopilotQuotaBucket {
  /** 0–100. */
  percentRemaining: number;
  unlimited: boolean;
  /** Incluído no plano por mês. */
  entitlement?: number;
  /** Usado no mês (inclui o excedente). */
  used?: number;
  /** A conta pode passar do incluído (cobrança extra ligada). */
  overagePermitted?: boolean;
  overageUsed?: number;
  /** Teto do excedente. */
  overageLimit?: number;
}

/** Cota da conta do GitHub Copilot, lida do servidor oficial (`checkQuota`). */
export interface CopilotQuota {
  /** "free", "individual" (Pro), "individual_pro" (Pro+), "business", "enterprise"… */
  plan: string | null;
  /** A conta pode mudar para um plano com mais modelos. */
  canUpgrade?: boolean;
  /** Quando a cota renova (AAAA-MM-DD). */
  resetDate: string | null;
  /** O instante exato da renovação (ISO, UTC), quando o GitHub informa. */
  resetAt: string | null;
  premium: CopilotQuotaBucket | null;
  chat: CopilotQuotaBucket | null;
  completions: CopilotQuotaBucket | null;
  /** Quando foi lido (ms). */
  at: number;
}

/** Modelo do chat do Copilot no catálogo da conta (`copilot/models`, o seletor do VS Code). */
export interface CopilotModelInfo {
  id: string;
  name: string;
  /** "Anthropic", "OpenAI", "Google"… */
  vendor?: string;
  /** "lightweight" · "versatile" · "powerful" (grupos do seletor). */
  category?: string;
  /** "low" · "medium" · "high". */
  price?: string;
  /** Níveis de raciocínio aceitos ("low", "medium", "high", "xhigh", "max"). */
  efforts?: string[];
  /** Consumo de pedidos premium (0 = incluído no plano). */
  multiplier?: number;
  premium?: boolean;
  preview: boolean;
  /** Liberado para a conta (falso: a conta ou a organização ainda não ativou). */
  enabled: boolean;
}

// ───────────── Motor VS Code (VSCodium) ─────────────

/**
 * Motor VS Code do Kompil Desktop: o servidor do VSCodium (código aberto do VS Code, licença MIT)
 * com o workbench e o host de extensões de verdade. As extensões do VS Code rodam nele como no
 * próprio VS Code; a loja é a Open VSX. Baixado na primeira vez (conferido pelo SHA-256).
 */
export const CODE_ENGINE = { version: "1.140.06880", sizeMb: 95 } as const;

export type CodeEngineState =
  | "missing"
  | "downloading"
  | "verifying"
  | "extracting"
  /** Copiando as extensões de outro editor (progress e message: qual e quantas). */
  | "importing"
  | "ready"
  | "starting"
  | "running"
  | "error"
  | "unsupported";

/** De onde vem o Claude Code oficial: extensão no motor do Kompil, extensão do VS Code ou instalado. */
export type ClaudeCodeSource = "engine" | "vscode" | "installed";

/** Conta do Claude Code oficial (o que o plugin do motor, o terminal e o VS Code usam). */
export interface ClaudeCodeAuth {
  /** O Claude Code está instalado (na extensão do motor, na do VS Code ou na máquina). */
  available: boolean;
  loggedIn: boolean;
  /** "claude.ai" (assinatura) ou "console"/"api_key". */
  method?: string;
  /** pro, max, team, enterprise… */
  subscription?: string;
  email?: string;
  /** Organização da conta na Anthropic. */
  org?: string;
  /** Versão do Claude Code. */
  version?: string;
  source?: ClaudeCodeSource;
}

/** Tokens de um período (somados dos registros locais do Claude Code). */
export interface ClaudeCodeTokens {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  /** Respostas do Claude no período. */
  messages: number;
}

/** Uso do Claude Code nesta máquina (todas as pastas), pelos registros que ele guarda em ~/.claude. */
export interface ClaudeCodeUsage {
  /** Últimas 5 horas (a janela de sessão dos planos Pro/Max). */
  session: ClaudeCodeTokens;
  today: ClaudeCodeTokens;
  week: ClaudeCodeTokens;
  /** Últimos 7 dias por modelo. */
  byModel: Array<{ model: string } & ClaudeCodeTokens>;
  /** Primeira resposta da janela de 5 horas (ms) — a janela renova 5 h depois dela. */
  sessionStartedAt: number | null;
}

/** Limite do plano numa janela (como o "Session (5hr)" e "Weekly (7 day)" do plugin do VS Code). */
export interface ClaudePlanWindow {
  /** Quanto do limite já foi usado (0–100). */
  utilization: number;
  /** Quando renova (ms), se a Anthropic informou. */
  resetsAt: number | null;
}

export interface ClaudePlanUsage {
  /** Sessão: janela de 5 horas. */
  fiveHour: ClaudePlanWindow | null;
  /** Semana: janela de 7 dias. */
  sevenDay: ClaudePlanWindow | null;
  sevenDayOpus: ClaudePlanWindow | null;
  sevenDaySonnet: ClaudePlanWindow | null;
  subscription: string | null;
  /** Quando foi lido (ms). */
  at: number;
}

/** Uma conversa do Claude Code na pasta do projeto (para retomar com --resume). */
/** Conversa de outro app de IA (Copilot Chat do VS Code) no formato do histórico do Kompil. */
export interface ImportedChat {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  model: string | null;
  messages: Array<{ role: "user" | "assistant"; text: string; at?: number }>;
}

export interface ClaudeCodeSession {
  id: string;
  title: string;
  updatedAt: number;
  messages: number;
  tokens: number;
  model: string | null;
  /** Pasta (o fim do caminho) onde a conversa aconteceu, quando é de outra pasta. */
  folder?: string;
  /** A conversa é deste projeto (já dá para retomar aqui). */
  here?: boolean;
}

export interface ClaudeCodeLoginResult {
  ok: boolean;
  error?: string;
  auth?: ClaudeCodeAuth;
}

/** Ao abrir no motor: o que mostrar logo de cara. */
/** O que o painel do Claude pede ao abrir: a última conversa (padrão), uma nova ou uma anterior. */
export interface ClaudeEmbedRequest {
  /** Retoma esta conversa (id da sessão do Claude Code). */
  session?: string;
  /** Começa uma conversa nova. */
  new?: boolean;
}

/** Posição do painel na janela (px da página). */
export interface EmbedRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CodeEngineStatus {
  state: CodeEngineState;
  version: string;
  /** Andamento do download ou da importação (0–1). */
  progress?: number;
  /** O motor veio com o instalador do Kompil (não precisa baixar). */
  bundled?: boolean;
  message?: string;
  /** Extensões instaladas no motor. */
  extensions: number;
  /**
   * Alguma extensão foi habilitada ou desabilitada com o motor rodando: vale depois de reiniciar as
   * extensões (como o "Restart Extensions" do VS Code).
   */
  restartNeeded?: boolean;
}

export interface CodeEngineExtension {
  id: string;
  version: string;
  name?: string;
  publisher?: string;
  /** Aviso (ex.: extensão da Microsoft licenciada só para o VS Code oficial). */
  warning?: string;
  description?: string;
  /** Ícone da extensão (data: URL lido da pasta dela). */
  icon?: string;
  /** Desabilitada (o motor sobe sem ela). Ausente nas versões antigas do app: habilitada. */
  enabled?: boolean;
  /**
   * De onde vem: "engine" (instalada no motor pelo Kompil) ou o editor ligado ("vscode", "cursor"…),
   * cuja pasta o motor usa sem copiar.
   */
  origin?: string;
  categories?: string[];
  /** Quando foi instalada (ms). */
  installedAt?: number;
  preview?: boolean;
}

/** Ferramenta de uma extensão do motor (contêiner da barra de atividades do VS Code). */
export interface EngineTool {
  /** "publicador.nome/contêiner". */
  id: string;
  extensionId: string;
  title: string;
  /** Ícone (data: URL). */
  icon?: string;
  /** Ícone em silhueta (pintado na cor do texto, como os da barra do VS Code). */
  mono?: boolean;
  /** Vistas da ferramenta e o contêiner de origem de cada uma. */
  views: Array<{ id: string; home: string }>;
}

/** O que uma extensão instalada traz (aba Recursos do VS Code) e os textos dela. */
export interface CodeEngineExtensionDetails {
  id: string;
  readme: string | null;
  changelog: string | null;
  license: string | null;
  repository: string | null;
  homepage: string | null;
  bugs: string | null;
  /** Tamanho da pasta, em bytes. */
  sizeBytes: number | null;
  /** Versão mínima do VS Code (engines.vscode). */
  engine: string | null;
  contributes: {
    commands: Array<{ id: string; title: string }>;
    settings: Array<{ key: string; description: string }>;
    languages: Array<{ id: string; extensions: string[] }>;
    themes: string[];
    iconThemes: string[];
    snippets: string[];
    keybindings: Array<{ command: string; key: string }>;
    views: string[];
  };
}

/** Extensão do catálogo (Open VSX, a loja do VSCodium). */
export interface GalleryExtension {
  /** "publicador.nome" em minúsculas. */
  id: string;
  namespace: string;
  name: string;
  displayName: string;
  description: string;
  version: string;
  /** Nome de exibição do publicador. */
  publisher: string;
  /** Publicador verificado (o selo do VS Code). */
  verified: boolean;
  iconUrl: string | null;
  downloads: number;
  /** Média das avaliações (0–5), ou null sem avaliações. */
  rating: number | null;
  reviews: number;
  /** Data da última versão (ISO). */
  updatedAt: string | null;
  preview?: boolean;
}

export interface GalleryExtensionDetails extends GalleryExtension {
  categories: string[];
  tags: string[];
  repository: string | null;
  homepage: string | null;
  bugs: string | null;
  license: string | null;
  /** Versões publicadas, da mais nova para a mais antiga (até 30). */
  versions: string[];
  dependencies: string[];
  extensionPack: string[];
  readme: string | null;
  changelog: string | null;
  /** Página da extensão na loja. */
  url: string;
  engine: string | null;
}

export type GallerySort = "relevance" | "downloadCount" | "rating" | "timestamp";

export interface GallerySearch {
  query: string;
  sort?: GallerySort;
  category?: string;
  offset?: number;
  size?: number;
}

export interface GallerySearchResult {
  total: number;
  extensions: GalleryExtension[];
}

/** Extensão de um editor deste computador que dá para trazer ao motor (a lista para escolher). */
export interface CodeEngineCandidate {
  /** Editor de onde vem ("vscode", "cursor"…). */
  source: string;
  id: string;
  version: string;
  name?: string;
  publisher?: string;
  description?: string;
  warning?: string;
  /** Já está no motor (vinda deste editor). */
  linked: boolean;
}

export interface CodeEngineImport {
  /** Trazidas do editor (o motor usa a pasta do próprio editor, na mesma versão). */
  copied: string[];
  /** Já estavam no motor, iguais. */
  skipped: string[];
  failed: Array<{ id: string; error: string }>;
  /** Trazidas, mas com licença que só permite o VS Code da Microsoft (podem não ativar). */
  limited: string[];
}

/**
 * Extensões da Microsoft cuja licença só permite os produtos dela (VS Code, Visual Studio): são
 * trazidas como as outras, mas costumam recusar rodar em editores feitos com o código aberto
 * (VSCodium, Cursor, Windsurf, Kompil).
 */
export const MS_ONLY_EXTENSIONS =
  /^(ms-python\.vscode-pylance|ms-vscode-remote\..+|ms-vsliveshare\..+|ms-dotnettools\.(csdevkit|vscodeintellicode-csharp|csharp)|visualstudioexptteam\..+|ms-vscode\.cpptools(-extension-pack)?|ms-vscode\.remote-.+|ms-azuretools\.vscode-containers)$/i;

export const MS_ONLY_WARNING =
  "Extensão da Microsoft licenciada só para o VS Code oficial: pode não ativar em editores de código aberto.";

// ───────────── Importar do VS Code ─────────────

export interface VsCodeInstall {
  source: "vscode" | "vscode-insiders" | "cursor" | "vscodium" | "windsurf";
  /** Extensões instaladas (publisher.nome, minúsculas). */
  extensions: string[];
  /** "workbench.colorTheme" do settings.json, se houver. */
  theme: string | null;
}

// ───────────── Execuções locais e Kompil Engine (contêineres) ─────────────

export type LocalRunKind = "live" | "container";
/** starting: preparando (arquivos, imagem); running: processo no ar; exited/failed: terminou. */
export type LocalRunStatus = "starting" | "running" | "stopping" | "exited" | "failed";

/** Uma execução na máquina da pessoa (Live Server ou contêiner). Só ela vê e só ela controla. */
export interface LocalRun {
  id: string;
  projectId: string;
  projectName: string;
  kind: LocalRunKind;
  /** "Live Server", "Dockerfile", "node:22-alpine", "docker-compose.yml"… */
  label: string;
  status: LocalRunStatus;
  /** O endereço respondeu (HTTP) pelo menos uma vez. */
  reachable: boolean;
  url: string | null;
  ports: number[];
  startedAt: number;
  /** Motivo do erro ou código de saída. */
  detail: string | null;
  /** Pasta do projeto que é servida ou montada ("" = raiz). */
  root: string;
  /** Esta janela mantém os arquivos em dia (as edições do time chegam por ela). */
  mine: boolean;
}

/** Como rodar o projeto num contêiner (detectado pela página, conferido pela pessoa). */
export interface ContainerSpec {
  /** dockerfile: build + run; image: imagem pronta com o projeto montado; compose: compose up. */
  mode: "dockerfile" | "image" | "compose";
  /** Dockerfile ou arquivo do Compose (caminho no projeto). */
  file?: string;
  image?: string;
  /** Comando no contêiner (modo image), rodado com sh -c. */
  command?: string;
  /** Onde o projeto é montado no contêiner (modo image; padrão /app). */
  mountAt?: string;
  /** Porta do app dentro do contêiner. */
  containerPort?: number | null;
  /** Porta em localhost (a primeira livre a partir desta). */
  hostPort?: number | null;
  /** Compose: portas publicadas pelo arquivo (para o endereço e o aviso de "no ar"). */
  ports?: number[];
}

export interface LocalRunStart {
  /** Gerado pela página (UUID): ela já sabe qual execução está mantendo em dia. */
  id: string;
  projectId: string;
  projectName: string;
  root: string;
  files: Record<string, string>;
}

export type EngineState = "checking" | "unsupported" | "missing" | "installing" | "ready" | "error";

/** Kompil Engine: contêineres sem Docker Desktop (WSL 2 + Podman no Windows; Podman/Docker no Linux e macOS). */
export interface EngineStatus {
  state: EngineState;
  backend: "wsl" | "podman" | "docker" | null;
  /** Versão do Podman/Docker. */
  version: string | null;
  /** Explicação para a pessoa (o que falta, o erro). */
  detail: string | null;
  progress: { step: string; percent: number | null } | null;
  /** Windows sem WSL: o app pode ativá-lo (pede administrador e reinício). */
  canEnableWsl: boolean;
}

// ───────────── Git com qualquer servidor (HTTPS) ─────────────

export type GitRemoteResult<T> = { ok: true; data: T } | { ok: false; status: number; error: string };

export interface GitRemoteCommit {
  sha: string;
  message: string;
  author: string;
  login: string | null;
  date: string;
  url: string;
}

/** Atualização do app: dev (não instalado), procurando, disponível, baixando, pronta, erro. */
export interface UpdateStatus {
  /** "installing": o app vai fechar para instalar (a página avisa antes). */
  state: "dev" | "idle" | "checking" | "available" | "downloading" | "ready" | "installing" | "error";
  version: string | null;
  percent: number | null;
  error: string | null;
}

// ───────────── Projeto na máquina (cópia local, terminal local, importar pasta) ─────────────

export interface LocalFileChange {
  path: string;
  /** null = apagado. */
  content: string | null;
}

export interface LocalProjectInfo {
  enabled: boolean;
  /** Pasta da cópia local (null: ainda não ligada). */
  path: string | null;
  /** Pasta sugerida (Documentos/Kompil/<projeto>). */
  suggested: string;
}

export interface LocalImportProgress {
  phase: "listing" | "reading";
  done: number;
  total: number;
}

export interface ImportedFolder {
  importId: string;
  name: string;
  files: Record<string, string>;
  /** Arquivos que ficaram só no disco (binários, grandes demais, .env). */
  skipped: number;
  /** Quais ficaram só no disco e por quê (binário, grande demais, acima do limite de arquivos). */
  left?: Array<{ path: string; reason: "binary" | "large" | "limit"; bytes: number }>;
  /** Remoto "origin" do .git da pasta, se houver. */
  git: { url: string; branch: string | null } | null;
}

export interface KompilDesktopApi {
  readonly platform: "win32" | "darwin" | "linux";
  readonly version: string;
  /** Comando de menu ou atalho nativo para esta janela. Devolve a função que cancela. */
  onCommand(listener: (command: DesktopCommand) => void): () => void;
  /** Tela cheia mudou (no macOS, os botões da janela somem em tela cheia). */
  onFullScreen(listener: (fullScreen: boolean) => void): () => void;
  /**
   * Botões da janela desenhados pela página (minimizar, maximizar/restaurar, fechar), no lugar dos
   * do sistema. Só existe nas versões do app que desenham a barra assim (as antigas usam os nativos).
   */
  windowControls?: {
    minimize(): void;
    toggleMaximize(): void;
    close(): void;
    isMaximized(): Promise<boolean>;
    onMaximized(listener: (maximized: boolean) => void): () => void;
  };
  /** Salvar um arquivo no computador (Salvar como…). Devolve o caminho ou null se cancelou. */
  saveFileAs?(suggestedName: string, content: string): Promise<string | null>;
  /** Ações nativas (desfazer, zoom, tela cheia…) na janela atual. */
  runRole(role: DesktopRole): void;
  /**
   * Tira a janela de capturas e gravações de tela (visualizadores de um projeto). Versões antigas do
   * app não têm.
   */
  setCaptureBlocked?(blocked: boolean): void;
  /**
   * Cor do ícone do app (janela, barra de tarefas, bandeja, Dock e atalhos da área de trabalho e do
   * menu Iniciar). Versões antigas do app não têm.
   */
  appIcon?: {
    get(): Promise<AppIconColor>;
    set(color: AppIconColor): Promise<{ ok: boolean }>;
    /** Trocada por fora da página (menu do ícone na bandeja). */
    onChange(listener: (color: AppIconColor) => void): () => void;
  };
  /** Comandos do processo principal (nova janela, sobre, atualizações). */
  runMainCommand(command: DesktopCommand): void;
  /** Comandos que esta janela consegue executar agora (habilita/desabilita o menu nativo). */
  setAvailableCommands(commands: DesktopCommand[]): void;
  /** Abre um caminho do app (ex.: "/p/<id>") numa janela nova. */
  openWindow(path: string): void;
  /** Abre um link http(s) no navegador do sistema. */
  openExternal(url: string): void;
  /** Login com GitHub/Google no navegador do sistema (RFC 8252); a sessão volta para o app em `next`. */
  login(provider: "github" | "google", next?: string): Promise<{ ok: boolean; error?: string }>;
  /** Mensagens não lidas: contador no ícone (Dock, barra de tarefas). */
  setUnread(count: number): void;
  /** Pinta a barra de título nativa com as cores do tema da página. */
  setTheme(colors: DesktopThemeColors): void;
  /** Traz a janela para a frente (ex.: clique numa notificação). */
  focus(): void;
  /** Notificação do sistema (Windows, macOS, Linux); o clique traz a janela e chama onNotificationClick. */
  notify?(n: { id: string; title: string; body?: string; icon?: string | null }): void;
  onNotificationClick?(listener: (id: string) => void): () => void;
  /** Zoom da janela (1 = 100%), o mesmo de Ctrl+= e Ctrl+-; fica lembrado. */
  zoom: {
    get(): Promise<number>;
    set(factor: number): void;
    onChange(listener: (factor: number) => void): () => void;
  };
  getServer(): Promise<string>;
  /** Troca o servidor (https, ou http só em localhost) e recarrega as janelas. */
  setServer(url: string): Promise<{ ok: boolean; error?: string }>;
  /** Tela "sem conexão": tentar de novo agora. */
  retry(): void;
  /** GitHub Copilot oficial (o servidor sobe na primeira chamada). */
  copilot: {
    status(): Promise<CopilotStatus>;
    onStatus(listener: (status: CopilotStatus) => void): () => void;
    signIn(): Promise<CopilotSignIn>;
    signOut(): Promise<void>;
    /** Conversas do Copilot Chat do VS Code neste computador (para continuar no Kompil). */
    vscodeChats?(): Promise<ImportedChat[]>;
    complete(request: CopilotRequest): Promise<CopilotItem[]>;
    shown(item: CopilotItem): void;
    accepted(item: CopilotItem): void;
    /** Cota da conta (null sem conta conectada). */
    quota(): Promise<CopilotQuota | null>;
    /** O servidor avisou que a cota mudou (depois de usar o Copilot). */
    onQuota(listener: (quota: CopilotQuota) => void): () => void;
    /** Modelos do chat da conta, com categoria e raciocínio (versões antigas do app não têm). */
    models?(): Promise<CopilotModelInfo[]>;
  };
  /** Git: o token do GitHub fica no cofre do sistema (criptografado); a página nunca o vê. */
  git: {
    status(): Promise<{ connected: boolean; login?: string; name?: string; avatarUrl?: string }>;
    connect(token: string): Promise<{ ok: boolean; login?: string; name?: string; error?: string }>;
    disconnect(): Promise<void>;
    /**
     * "Conectar ao GitHub" pelo navegador: pede o código que a pessoa confirma em
     * github.com/login/device (versões antigas do app não têm).
     */
    deviceStart?(clientId: string): Promise<{
      ok: boolean;
      userCode?: string;
      verificationUri?: string;
      expiresIn?: number;
      error?: string;
    }>;
    /** Espera a autorização no navegador; ao autorizar, o token vai para o cofre do sistema. */
    deviceWait?(): Promise<{
      ok: boolean;
      login?: string;
      name?: string;
      avatarUrl?: string;
      error?: string;
    }>;
    deviceCancel?(): Promise<void>;
    /** Chamada à API do GitHub (só api.github.com, só /user e /repos). */
    api<T = unknown>(method: string, path: string, body?: unknown): Promise<{ status: number; data: T }>;
  };
  /**
   * Chat com agentes (GitHub Copilot pelo modo ACP do servidor oficial). O agente trabalha num
   * espelho local do projeto; o que ele cria ou altera volta como evento "file".
   */
  /** Motor VS Code (extensões do VS Code de verdade). Versões antigas do app não têm. */
  codeEngine?: {
    status(): Promise<CodeEngineStatus>;
    /** Baixa e prepara o motor (uma vez); o andamento chega por onStatus. */
    install(): Promise<CodeEngineStatus>;
    /** As extensões dos editores deste computador, para a pessoa escolher quais trazer. */
    candidates(): Promise<CodeEngineCandidate[]>;
    /**
     * Traz para o motor as extensões de um editor (VS Code, Cursor…), menos as de `exclude` — que
     * saem do motor se já estavam. A escolha vale nas próximas sincronizações.
     */
    importExtensions(source: VsCodeInstall["source"], exclude?: string[]): Promise<CodeEngineImport>;
    extensions(): Promise<CodeEngineExtension[]>;
    /** Instala pela Open VSX ("publisher.nome"), opcionalmente numa versão. */
    installExtension(id: string, version?: string): Promise<{ ok: boolean; error?: string }>;
    /** Desinstala (as ligadas a outro editor só saem do motor: a pasta do editor fica). */
    uninstallExtension?(id: string): Promise<{ ok: boolean; error?: string }>;
    /** Habilita ou desabilita (vale ao reiniciar as extensões, se o motor estiver rodando). */
    setExtensionEnabled?(id: string, enabled: boolean): Promise<{ ok: boolean; restartNeeded: boolean }>;
    /** Reinicia o servidor do motor (as janelas abertas recarregam), aplicando o que mudou. */
    restartExtensions?(): Promise<{ ok: boolean; error?: string }>;
    /** README, CHANGELOG e o que a extensão instalada traz (comandos, configurações…). */
    extensionDetails?(id: string): Promise<CodeEngineExtensionDetails | null>;
    /**
     * Temas e snippets da extensão instalada, no formato das extensões importadas do VS Code: o tema
     * instalado pela tela de Extensões entra na conta e pinta a IDE inteira.
     */
    extensionContent?(id: string): Promise<ImportedExtension | null>;
    /** Versões novas na Open VSX das extensões instaladas no motor (id → versão). */
    updates?(): Promise<Record<string, string>>;
    /** Catálogo de extensões (Open VSX). */
    gallery?: {
      search(query: GallerySearch): Promise<GallerySearchResult>;
      details(id: string): Promise<GalleryExtensionDetails | null>;
    };
    /**
     * O plugin Claude Code oficial dentro do painel do Kompil: o motor nunca vira janela; só a tela
     * do Claude aparece, presa à posição do painel (`embedBounds`). `files` vira a pasta que o
     * Claude edita (e o que ele muda volta para o projeto).
     */
    embedClaude?(
      projectId: string,
      files: Record<string, string>,
      request?: ClaudeEmbedRequest,
    ): Promise<{ ok: boolean; error?: string; ready?: boolean }>;
    /**
     * Ferramentas das extensões habilitadas (o que cada uma põe na barra de atividades do VS Code):
     * aparecem na barra da esquerda do Kompil.
     */
    tools?(): Promise<EngineTool[]>;
    /**
     * Se a pessoa autorizou trazer as extensões do VS Code ("yes"), recusou ("no") ou ainda não
     * respondeu (null), e quantas extensões os editores desta máquina têm.
     */
    vscodeConsent?(): Promise<{ decision: "yes" | "no" | null; available: number } | null>;
    /** "Agora não": tira do motor as extensões dos editores e não pergunta de novo sozinho. */
    declineVscode?(): Promise<void>;
    /**
     * Mostra a ferramenta no painel da esquerda (a tela das ferramentas do projeto, presa ao painel
     * por `embedBounds(projectId + "#tools", …)`).
     */
    embedTool?(
      projectId: string,
      files: Record<string, string>,
      toolId: string,
    ): Promise<{ ok: boolean; error?: string; ready?: boolean }>;
    /** A tela do Claude daquele projeto já existe (`open`) e o plugin já está nela (`ready`)? */
    embedState?(projectId: string): Promise<{ open: boolean; ready: boolean }>;
    /** Onde o painel está na janela (null: escondido). */
    embedBounds?(projectId: string, rect: EmbedRect | null): void;
    /** O plugin do Claude abriu na tela embutida (a tela pode aparecer). */
    onEmbedReady?(listener: (projectId: string) => void): () => void;
    /** O Claude abriu um arquivo do projeto (link na conversa): abre no editor do Kompil. */
    onOpenFile?(listener: (e: { projectId: string; path: string; line: number }) => void): () => void;
    /** Um arquivo do projeto mudou (do time ou do Kompil): vai para a pasta do motor. */
    write(projectId: string, path: string, content: string | null): void;
    close(projectId: string): void;
    onStatus(listener: (status: CodeEngineStatus) => void): () => void;
    /** O motor (o VS Code ou uma extensão) criou, mudou ou apagou um arquivo do projeto. */
    onFile(
      listener: (event: { projectId: string; path: string; content: string | null }) => void,
    ): () => void;
    /** A janela do motor deste projeto fechou. */
    onClosed(listener: (projectId: string) => void): () => void;
  };
  /**
   * Login da assinatura do Claude para o plugin Claude Code do motor: o Claude Code oficial abre a
   * autorização da Anthropic no navegador; nenhuma janela de IDE abre.
   */
  claudeCode?: {
    status(): Promise<ClaudeCodeAuth>;
    /** `claudeai` (assinatura, padrão) ou `console` (conta do Console, uso da API). */
    login(method?: "claudeai" | "console"): Promise<ClaudeCodeLoginResult>;
    /** Código que a página da Anthropic mostrou (quando o navegador não volta sozinho). */
    submitCode?(code: string): Promise<boolean>;
    logout?(): Promise<ClaudeCodeAuth>;
    /** Conversas do Claude Code na pasta (cópia local) do projeto, mais recentes primeiro. */
    sessions?(projectId: string): Promise<ClaudeCodeSession[]>;
    /** Conversas do Claude Code de todas as pastas deste computador (inclusive as do VS Code). */
    allSessions?(projectId: string): Promise<ClaudeCodeSession[]>;
    /** Traz uma conversa de outra pasta para o projeto (o Claude daqui retoma de onde parou). */
    importSession?(projectId: string, sessionId: string): Promise<boolean>;
    /** Uso de tokens nesta máquina (5 h, hoje, 7 dias e por modelo). */
    usage?(): Promise<ClaudeCodeUsage>;
    /** Limites do plano (5 h e 7 dias, com o horário de renovação), pelo próprio Claude Code. */
    planUsage?(): Promise<ClaudePlanUsage | null>;
    cancel(): void;
    /** Link da autorização (para "o navegador não abriu? clique aqui"). */
    onLoginUrl(listener: (url: string) => void): () => void;
  };
  agent: {
    /** Sobe o agente antes da primeira mensagem (versões antigas do app não têm). */
    warm?(): void;
    /** Cria a conversa do projeto (espelho em disco com `files`: caminho → conteúdo). */
    start(provider: AgentProvider, projectId: string, files: Record<string, string>): Promise<AgentSession>;
    /** Atualiza o espelho (antes de cada pedido). */
    sync(projectId: string, files: Record<string, string>): Promise<void>;
    prompt(sessionId: string, text: string): Promise<{ stopReason: string; usage?: AgentUsage }>;
    cancel(sessionId: string): void;
    /** Resposta a um pedido de permissão (null = cancelado). */
    respond(requestId: number, optionId: string | null): void;
    setModel(sessionId: string, modelId: string): Promise<boolean>;
    /** Muda uma opção da sessão (nível de raciocínio, modo). Devolve as opções atualizadas. */
    setConfig(sessionId: string, configId: string, value: string): Promise<AgentConfigOption[] | null>;
    /** Avisa que o projeto fechou (para o observador do espelho). */
    close(projectId: string): void;
    onEvent(listener: (event: AgentEvent) => void): () => void;
  };
  /**
   * Claude com a conta da Anthropic da pessoa (chave da API do console da Anthropic, guardada no
   * cofre do sistema). As chamadas saem deste computador; a página nunca vê a chave.
   */
  claude: {
    status(): Promise<{ connected: boolean; hint?: string }>;
    connect(apiKey: string): Promise<{ ok: boolean; error?: string }>;
    disconnect(): Promise<void>;
    /** Resolve quando a resposta termina; o texto chega pelos eventos. */
    chat(request: ClaudeChatRequest): Promise<void>;
    cancel(requestId: string): void;
    onEvent(listener: (event: ClaudeEvent) => void): () => void;
  };
  /**
   * Agente de código com a chave de IA da pessoa (ADR 0025). As chaves ficam no cofre do sistema
   * (a da Anthropic é a mesma do chat); a página manda a conversa e recebe a resposta, e os
   * comandos rodam na pasta da cópia local do projeto, só depois da permissão da pessoa.
   */
  assistant: {
    providers(): Promise<AssistantProviderStatus[]>;
    connect(
      provider: AssistantProviderId,
      /** `model`: o que a pessoa escolheu (ex.: "thinkingmachines/inkling-small:free"); sem ele, automático. */
      input: { key?: string; baseUrl?: string; model?: string },
    ): Promise<{
      ok: boolean;
      error?: string;
      /** Modelo já definido: o da pessoa, ou o escolhido pela API. */
      model?: string;
      /** A pessoa escolheu este modelo (o agente nunca troca sozinho). */
      manual?: boolean;
      note?: string;
      /** O modelo escolhido pela pessoa não funcionou no teste: por quê, e uma alternativa. */
      problem?: { message: string; alternative?: string };
    }>;
    disconnect(provider: AssistantProviderId): Promise<void>;
    /**
     * Escolhe o modelo sozinho, pela API: lê a conta (no OpenRouter, sem créditos usa os grátis),
     * testa os recomendados para programar e devolve o primeiro que respondeu. `exclude`: os que
     * já falharam.
     */
    pickModel(provider: AssistantProviderId, exclude?: string[]): Promise<AssistantModelPick>;
    /** Modelos de conversa que a chave enxerga. */
    models(provider: AssistantProviderId): Promise<AssistantModelList>;
    /** De qual provedor é esta chave (testada em cada um, sem guardar): a pessoa só confirma. */
    detect(key: string): Promise<AssistantDetectResult>;
    /** Conexões de IA salvas (nome, provedor, modelo; a chave só ofuscada). Versões antigas não têm. */
    connections?(): Promise<AssistantConnection[]>;
    /**
     * Confere a chave colada (ou o endereço, para Ollama e compatíveis) sem salvar: provedor, modelos
     * de texto da conta e o modelo sugerido, para a pessoa confirmar.
     */
    inspectConnection?(input: {
      key?: string;
      baseUrl?: string;
      provider?: AssistantProviderId;
    }): Promise<AssistantInspectResult>;
    /** Salva a conexão (o modelo precisa ser de texto e existir na conta; senão, erro na hora). */
    saveConnection?(input: {
      name: string;
      key?: string;
      baseUrl?: string;
      provider: AssistantProviderId;
      model: string;
    }): Promise<{ ok: true; connection: AssistantConnection } | { ok: false; error: string }>;
    updateConnection?(
      id: string,
      patch: { name?: string; model?: string },
    ): Promise<{ ok: true; connection: AssistantConnection } | { ok: false; error: string }>;
    removeConnection?(id: string): Promise<void>;
    /** Um turno do modelo (texto e chamadas de ferramenta); o texto chega antes pelos eventos. */
    turn(request: AssistantTurnRequest): Promise<AssistantTurnResult>;
    /** Comando na pasta da cópia local do projeto (a saída chega pelos eventos). */
    exec(request: AssistantExecRequest): Promise<AssistantExecResult>;
    /** Cancela um turno ou um comando. */
    cancel(requestId: string): void;
    onEvent(listener: (event: AssistantEvent) => void): () => void;
  };
  /** Versão nova publicada pelo painel: aviso, baixar e instalar com um clique. */
  update: {
    status(): Promise<UpdateStatus>;
    check(): Promise<void>;
    /** Baixa (conferindo o SHA-512) e reinicia instalando. */
    install(): Promise<void>;
    onStatus(listener: (status: UpdateStatus) => void): () => void;
  };
  /** VS Code (e Cursor, VSCodium, Windsurf) instalados neste computador, com as extensões. */
  vscode: {
    detect(): Promise<VsCodeInstall[]>;
    /** Temas e snippets das extensões do editor (convertidos) e a classificação de cada uma. */
    readExtensions(source: VsCodeInstall["source"]): Promise<ImportedExtension[]>;
  };
  /**
   * Git com qualquer servidor por HTTPS (GitLab, Bitbucket, Azure DevOps, Gitea, GitHub…), sem o
   * Git instalado. `url` é o endereço normalizado (https://servidor/caminho). As credenciais
   * (usuário + token, por servidor) ficam no cofre do sistema.
   */
  gitRemote: {
    /** Endereço normalizado (git@host:caminho vira https) ou null se não for um repositório válido. */
    normalize(url: string): Promise<string | null>;
    info(url: string): Promise<GitRemoteResult<{ defaultBranch: string | null; branches: string[] }>>;
    head(url: string, branch: string): Promise<GitRemoteResult<string | null>>;
    files(
      url: string,
      branch: string,
      commit: string,
    ): Promise<
      GitRemoteResult<{
        tree: string;
        files: Record<string, { sha: string; size: number }>;
        truncated: boolean;
      }>
    >;
    /** Texto do blob (null se binário). */
    blob(url: string, branch: string, sha: string): Promise<GitRemoteResult<string | null>>;
    /** Commit sobre `parent` e push (só avança). Devolve o SHA do commit. */
    commitPush(input: {
      url: string;
      branch: string;
      parent: string | null;
      changes: Array<{ path: string; content: string | null }>;
      message: string;
      author: { name: string; email: string };
    }): Promise<GitRemoteResult<string>>;
    log(url: string, branch: string, limit: number): Promise<GitRemoteResult<GitRemoteCommit[]>>;
    status(
      url: string,
      branch: string,
      base: string,
    ): Promise<GitRemoteResult<{ behind: number; diverged: boolean }>>;
    credentials: {
      /** Servidores com credencial salva (origens). */
      list(): Promise<string[]>;
      set(url: string, username: string, password: string): Promise<{ ok: boolean; error?: string }>;
      remove(url: string): Promise<void>;
    };
  };
  /**
   * Execuções locais: vários projetos ao mesmo tempo (Live Servers e contêineres), cada um na sua
   * porta. Rodam só nesta máquina; o código continua compartilhado com o time.
   */
  runs: {
    list(): Promise<LocalRun[]>;
    onChange(listener: (runs: LocalRun[]) => void): () => void;
    /** Live Server do projeto (um por projeto): abre o navegador. */
    startLive(input: LocalRunStart): Promise<{ ok: boolean; run?: LocalRun; error?: string }>;
    /** Contêiner do projeto no Kompil Engine (um por projeto). */
    startContainer(
      input: LocalRunStart & { spec: ContainerSpec },
    ): Promise<{ ok: boolean; run?: LocalRun; error?: string }>;
    /** Arquivos alterados (null = apagado). */
    update(runId: string, changes: Record<string, string | null>): void;
    /** Todos os arquivos de novo (a página recarregou e retomou a execução). */
    resync(runId: string, files: Record<string, string>): Promise<void>;
    stop(runId: string): Promise<void>;
    /** Tira da lista uma execução que já terminou. */
    dismiss(runId: string): void;
    /** Saída guardada (as últimas linhas); `end` = posição total (para juntar com os eventos). */
    logs(runId: string): Promise<{ text: string; end: number }>;
    /** Trecho novo da saída; `end` = posição total depois dele. */
    onLog(listener: (event: { runId: string; text: string; end: number }) => void): () => void;
    open(runId: string): void;
  };
  /**
   * Cópia local do projeto: a pasta na máquina, salva a cada alteração (nos dois sentidos), base do
   * terminal local e do trabalho sem internet.
   */
  local: {
    get(projectId: string, projectName: string): Promise<LocalProjectInfo>;
    /** Liga a cópia local (choose: a pessoa escolhe a pasta no diálogo do sistema). */
    enable(
      projectId: string,
      projectName: string,
      choose: boolean,
    ): Promise<{ ok: boolean; path?: string; error?: string }>;
    disable(projectId: string): Promise<void>;
    /** Projeto aberto: reconcilia pasta e projeto; devolve o que mudou no disco (para o projeto). */
    start(
      projectId: string,
      files: Record<string, string>,
      /**
       * `secrets`: dono do projeto — os .env da pasta também sobem. `unknown`: arquivos do projeto
       * que não carregaram (a pasta não mexe neles).
       */
      opts?: { secrets?: boolean; unknown?: string[] },
    ): Promise<{ toProject: LocalFileChange[]; conflicts: string[]; kept?: string[] } | null>;
    update(projectId: string, changes: LocalFileChange[]): void;
    stop(projectId: string): void;
    /** Abre a pasta no explorador do sistema. */
    reveal(projectId: string): void;
    onChanges(listener: (event: { projectId: string; changes: LocalFileChange[] }) => void): () => void;
    /** Arquivos binários do projeto que a pasta precisa (a página baixa e grava com `writeBinary`). */
    onDownloads?(
      listener: (event: {
        projectId: string;
        items: Array<{ path: string; sha256: string; size: number }>;
      }) => void,
    ): () => void;
    /** Bytes de um arquivo da pasta (binário ou grande, que vai para o projeto como arquivo). */
    readBinary?(projectId: string, path: string): Promise<Uint8Array | null>;
    /** Grava na pasta um arquivo binário baixado do projeto. */
    writeBinary?(projectId: string, path: string, data: Uint8Array, sha256: string): Promise<boolean>;
    /** Diálogo do sistema para escolher uma pasta de projeto existente (null: cancelou). */
    importFolder(): Promise<ImportedFolder | null>;
    /** Progresso de `importFolder`: listando a pasta, depois lendo `done` de `total` arquivos. */
    onImportProgress?(listener: (progress: LocalImportProgress) => void): () => void;
    /** A pasta importada vira a cópia local do projeto recém-criado. */
    adopt(projectId: string, importId: string): Promise<boolean>;
    /** Seleciona uma pasta para editar no Kompil, sem criar projeto ou sincronizar com a nuvem. */
    openFolder(): Promise<{ folderId: string; name: string } | null>;
    listFolder(folderId: string): Promise<{
      name: string;
      entries: Array<{ path: string; type: "file" | "folder" }>;
    }>;
    readFolderFile(folderId: string, path: string): Promise<string>;
    writeFolderFile(folderId: string, path: string, content: string): Promise<void>;
    closeFolder(folderId: string): void;
  };
  /** Terminal na sua máquina (como o do VS Code), na pasta da cópia local do projeto. */
  terminal: {
    create(
      projectId: string,
      cols: number,
      rows: number,
      /** `claude`: abre o Claude Code oficial em vez do shell (`resume`: retoma a conversa). */
      options?: { claude?: { resume?: string } },
    ): Promise<{ ok: true; id: string; shell: string; cwd: string } | { ok: false; error: string }>;
    write(id: string, data: string): void;
    resize(id: string, cols: number, rows: number): void;
    kill(id: string): void;
    onData(listener: (event: { id: string; data: string }) => void): () => void;
    onExit(listener: (event: { id: string; code: number }) => void): () => void;
  };
  /** Área de transferência do sistema (copiar ao selecionar e colar com o botão direito no terminal). */
  clipboard: {
    read(): Promise<string>;
    write(text: string): void;
  };
  /** Motor de contêineres embutido. */
  engine: {
    status(): Promise<EngineStatus>;
    onStatus(listener: (status: EngineStatus) => void): () => void;
    /** Baixa e prepara o motor (uma vez, ~100 MB). */
    install(): Promise<EngineStatus>;
    /** Apaga o motor e as imagens baixadas. */
    remove(): Promise<EngineStatus>;
    /** Windows: ativa o WSL (o sistema pede permissão de administrador). */
    enableWsl(): Promise<{ ok: boolean; error?: string }>;
  };
}
